/**
 * Token Store
 *
 * Store centralizzato per il token di autenticazione.
 * Permette a moduli standalone (come api.js) di accedere al token
 * senza dipendere direttamente da React context o MSAL.
 *
 * AuthContext aggiorna questo store quando ottiene un token.
 * api.js legge da questo store per le chiamate API.
 */

let currentToken = null;
let tokenRefreshCallback = null;

/**
 * Imposta il token corrente
 * @param {string|null} token - JWT token o null per logout
 */
export function setToken(token) {
  currentToken = token;
}

/**
 * Ottiene il token corrente
 * @returns {string|null} JWT token o null se non autenticato
 */
export function getToken() {
  return currentToken;
}

/**
 * Registra un callback per refresh del token
 * Chiamato quando il token è scaduto e serve un refresh
 * @param {Function} callback - Async function che ritorna un nuovo token
 */
export function setTokenRefreshCallback(callback) {
  tokenRefreshCallback = callback;
}

/**
 * Richiede un refresh del token
 * @returns {Promise<string|null>} Nuovo token o null se refresh fallisce
 */
export async function refreshToken() {
  if (tokenRefreshCallback) {
    try {
      const newToken = await tokenRefreshCallback();
      if (newToken) {
        currentToken = newToken;
      }
      return newToken;
    } catch (err) {
      console.error('[TokenStore] Token refresh failed:', err);
      return null;
    }
  }
  return null;
}

/**
 * Pulisce il token (logout)
 */
export function clearToken() {
  currentToken = null;
}

/**
 * Verifica se c'è un token valido
 * @returns {boolean}
 */
export function hasToken() {
  return currentToken !== null;
}

export default {
  setToken,
  getToken,
  setTokenRefreshCallback,
  refreshToken,
  clearToken,
  hasToken
};
